import React, { useEffect, useState, useRef, useCallback } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { initializeApp, getApps } from 'firebase/app';
import { getAuth } from 'firebase/auth';
import { getDatabase, ref, push, set, get, serverTimestamp } from 'firebase/database';
import QRCode from 'qrcode';

const FIREBASE_CONFIG = {
  apiKey:            'AIzaSyD10olyURDLvM2BdtkLTnwVVB98qfNiDcc',
  authDomain:        'clubswap-5a74a.firebaseapp.com',
  databaseURL:       'https://clubswap-5a74a-default-rtdb.firebaseio.com',
  projectId:         'clubswap-5a74a',
  storageBucket:     'clubswap-5a74a.firebasestorage.app',
  messagingSenderId: '274838532963',
  appId:             '1:274838532963:web:4044ba76f6192dcfdf5956',
};
const _app = getApps().length ? getApps()[0] : initializeApp(FIREBASE_CONFIG);

// ── CONFIG ────────────────────────────────────────────────────────────────
const PLATFORM_WALLET = process.env.REACT_APP_POLYGON_WALLET || '0xYourPolygonWalletAddressHere';
const RATE_REFRESH_SECONDS = 300; // 5 min
const BNB_RATE_API = 'http://localhost:3001/api/bnb-rate'; // your Express backend

const PACKAGES = [
  { diamonds: 10,   usdPrice: 1.00 },
  { diamonds: 50,   usdPrice: 4.50 },
  { diamonds: 100,  usdPrice: 8.00 },
  { diamonds: 500,  usdPrice: 35.00 },
  { diamonds: 1000, usdPrice: 65.00 },
];

// Generate unique fractional BNB amount per order for auto-matching
// e.g. 0.0120 BNB → 0.01213 BNB (unique per order)
function uniqueBNBAmount(usd, bnbPrice) {
  if (!bnbPrice || bnbPrice === 0) return null;
  const base = (usd + 0.15) / bnbPrice; // 15c platform buffer
  const offset = (Math.floor(Math.random() * 89) + 10) / 100000; // tiny unique offset
  return parseFloat((base + offset).toFixed(5));
}

function isValidTxHash(hash) {
  return /^0x[a-fA-F0-9]{64}$/.test(hash.trim());
}

export default function DepositPage() {
  const location = useLocation();
  const navigate = useNavigate();
  const auth = getAuth();
  const db = getDatabase();

  const incoming = location.state;
  const initialPkg = (incoming && incoming.package) ? incoming.package : PACKAGES[2];
  console.log("[DepositPage] pkg:", initialPkg);

  const [pkg] = useState(initialPkg);

  // BNB rate
  const [bnbPrice, setBnbPrice] = useState(null); // USD per 1 BNB
  const [exactAmount, setExactAmount] = useState(null); // BNB to send
  const [rateTimer, setRateTimer] = useState(RATE_REFRESH_SECONDS);
  const [rateLoading, setRateLoading] = useState(true);
  const exactAmountRef = useRef(null); // keep stable across re-renders

  // QR
  const qrCanvasRef = useRef(null);

  // TX submission
  const [txHash, setTxHash] = useState('');
  const [txError, setTxError] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [orderId, setOrderId] = useState('');

  // Copy states
  const [copied, setCopied] = useState(false);
  const [amtCopied, setAmtCopied] = useState(false);

  // ── Fetch BNB rate from our backend ────────────────────────────────────
  const fetchRate = useCallback(async () => {
    setRateLoading(true);
    try {
      const res = await fetch(BNB_RATE_API);
      const data = await res.json();
      const price = data.bnbUsd;
      setBnbPrice(price);
      // Only generate unique amount once per session (don't change on refresh)
      if (!exactAmountRef.current) {
        const amt = uniqueBNBAmount(pkg?.usdPrice, price);
        exactAmountRef.current = amt;
        setExactAmount(amt);
      }
    } catch (err) {
      console.error('Rate fetch failed:', err);
    } finally {
      setRateLoading(false);
      setRateTimer(RATE_REFRESH_SECONDS);
    }
  }, [pkg?.usdPrice]);

  useEffect(() => { fetchRate(); }, [fetchRate]);

  // Countdown timer
  useEffect(() => {
    const interval = setInterval(() => {
      setRateTimer(prev => {
        if (prev <= 1) { fetchRate(); return RATE_REFRESH_SECONDS; }
        return prev - 1;
      });
    }, 1000);
    return () => clearInterval(interval);
  }, [fetchRate]);

  // ── QR code ──────────────────────────────────────────────────────────
  useEffect(() => {
    if (!qrCanvasRef.current) return;
    const uri = PLATFORM_WALLET;
    QRCode.toCanvas(qrCanvasRef.current, uri, {
      width: 200,
      margin: 2,
      color: { dark: '#0a1a08', light: '#f0faf0' },
    }).catch(console.error);
  }, [exactAmount, PLATFORM_WALLET]);

  // ── Copy helpers ──────────────────────────────────────────────────────
  const copyAddress = () => {
    navigator.clipboard.writeText(PLATFORM_WALLET);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };
  const copyAmount = () => {
    if (!exactAmount) return;
    navigator.clipboard.writeText(exactAmount.toString());
    setAmtCopied(true);
    setTimeout(() => setAmtCopied(false), 2000);
  };

  // ── Submit TX hash ────────────────────────────────────────────────────
  const handleSubmit = async () => {
    setTxError('');
    const hash = txHash.trim();
    if (!hash) { setTxError('Please paste your transaction hash.'); return; }
    if (!isValidTxHash(hash)) { setTxError('Invalid format. Must be 0x followed by 64 hex characters.'); return; }
    // rate guard removed — backend matches by txHash

    const user = auth.currentUser;
    if (!user) { setTxError('Not logged in. Please refresh.'); return; }

    setSubmitting(true);
    try {
      const orderRef = push(ref(db, 'pendingDeposits'));
      const newOrderId = orderRef.key;
      const profileSnap = await get(ref(db, `users/${user.uid}/profile`));
      const telegramId = profileSnap.val()?.telegramId || null;
      await set(orderRef, {
        userId: user.uid,
        telegramId: telegramId,
        userEmail: user.email || '',
        diamonds: pkg?.diamonds,
        expectedUSD: pkg?.usdPrice,
        expectedPOL: exactAmount,
        network: 'polygon',
        token: 'POL',
        toWallet: PLATFORM_WALLET,
        txHash: hash,
        status: 'pending',
        createdAt: serverTimestamp(),
        creditedAt: null,
        receivedAmount: null,
        notes: '',
      });
      setOrderId(newOrderId);
      setSubmitted(true);
    } catch (err) {
      setTxError('Failed to save order. Please try again.');
      console.error(err);
    } finally {
      setSubmitting(false);
    }
  };

  // ── Helpers ───────────────────────────────────────────────────────────
  const formatTimer = (s) => `${Math.floor(s / 60)}:${String(s % 60).padStart(2, '0')}`;
  const isUrgent = rateTimer < 60;
  const polygonscanLink = txHash && isValidTxHash(txHash)
    ? `https://polygonscan.com/tx/${txHash.trim()}`
    : null;

  // ── STYLES ────────────────────────────────────────────────────────────
  const S = {
    page: { minHeight: '100vh', background: '#071309', color: '#e8f5e3', fontFamily: "'DM Sans', sans-serif", padding: '0 0 80px' },
    header: { display: 'flex', alignItems: 'center', gap: '12px', padding: '18px 20px', borderBottom: '1px solid #1a3018', background: '#071309', position: 'sticky', top: 0, zIndex: 10 },
    backBtn: { background: 'none', border: '1px solid #2a5028', color: '#7ecb6e', borderRadius: '8px', padding: '6px 14px', cursor: 'pointer', fontSize: '13px' },
    title: { fontSize: '16px', fontWeight: 600, color: '#e8f5e3', margin: 0 },
    container: { maxWidth: '480px', margin: '0 auto', padding: '24px 20px' },
    card: { background: '#0d2010', border: '1px solid #1e3a1c', borderRadius: '16px', padding: '20px', marginBottom: '16px' },
    label: { fontSize: '11px', fontWeight: 700, letterSpacing: '0.08em', color: '#4a8c42', textTransform: 'uppercase', marginBottom: '12px' },
    row: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '8px 0', borderBottom: '1px solid #162814' },
    rowLabel: { fontSize: '13px', color: '#7aaa72' },
    rowValue: { fontSize: '14px', fontWeight: 600, color: '#e8f5e3' },
    amountBox: { background: '#061008', border: `2px solid ${isUrgent ? '#ef4444' : '#2a5028'}`, borderRadius: '12px', padding: '18px', marginTop: '14px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', transition: 'border-color 0.3s' },
    amountValue: { fontSize: '26px', fontWeight: 800, color: isUrgent ? '#ef4444' : '#4ade80', letterSpacing: '-0.5px' },
    amountSub: { fontSize: '12px', color: '#4a8c42', marginTop: '3px' },
    copyBtn: { background: '#1a3c18', border: '1px solid #2a5028', color: '#7ecb6e', borderRadius: '8px', padding: '7px 14px', cursor: 'pointer', fontSize: '12px' },
    timerBox: {
      display: 'flex', alignItems: 'center', gap: '10px', marginTop: '12px',
      background: isUrgent ? '#2d0a0a' : '#061008',
      border: `1px solid ${isUrgent ? '#ef4444' : '#1e3a1c'}`,
      borderRadius: '10px', padding: '10px 14px', transition: 'all 0.3s',
    },
    timerDot: { width: '8px', height: '8px', borderRadius: '50%', background: isUrgent ? '#ef4444' : '#4ade80', animation: 'pulse 1s infinite', flexShrink: 0 },
    timerText: { fontSize: isUrgent ? '15px' : '13px', fontWeight: isUrgent ? 700 : 500, color: isUrgent ? '#ef4444' : '#4a8c42', flex: 1 },
    timerCount: { fontSize: isUrgent ? '20px' : '17px', fontWeight: 800, color: isUrgent ? '#ef4444' : '#4ade80', fontVariantNumeric: 'tabular-nums', minWidth: '42px', textAlign: 'right' },
    refreshBtn: { background: 'none', border: 'none', color: isUrgent ? '#ef4444' : '#4a8c42', cursor: 'pointer', fontSize: '12px', padding: 0 },
    qrWrapper: { background: '#f0faf0', padding: '14px', borderRadius: '12px', display: 'inline-block' },
    walletRow: { width: '100%', background: '#061008', border: '1px solid #1e3a1c', borderRadius: '10px', padding: '12px 14px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '10px' },
    walletAddr: { fontSize: '11px', color: '#7aaa72', fontFamily: 'monospace', wordBreak: 'break-all' },
    warning: { background: '#1a1000', border: '1px solid #5c3a00', borderRadius: '10px', padding: '12px 14px', fontSize: '12px', color: '#f59e0b', lineHeight: '1.7' },
    txInput: { width: '100%', background: '#061008', border: '1px solid #2a5028', borderRadius: '10px', padding: '12px 14px', color: '#e8f5e3', fontSize: '13px', fontFamily: 'monospace', outline: 'none', boxSizing: 'border-box', marginBottom: '8px' },
    txLink: { display: 'inline-flex', alignItems: 'center', gap: '6px', fontSize: '12px', color: '#4ade80', textDecoration: 'none', marginBottom: '12px' },
    errorText: { fontSize: '12px', color: '#f87171', marginBottom: '10px' },
    submitBtn: { width: '100%', padding: '15px', background: submitting ? '#1a3c18' : '#22c55e', color: submitting ? '#4a8c42' : '#071309', border: 'none', borderRadius: '12px', fontSize: '15px', fontWeight: 700, cursor: submitting ? 'not-allowed' : 'pointer' },
    successCard: { background: '#061a08', border: '1px solid #22c55e', borderRadius: '16px', padding: '28px 20px', textAlign: 'center' },
    orderId: { background: '#0d2010', border: '1px solid #1e3a1c', borderRadius: '8px', padding: '10px 14px', fontSize: '12px', fontFamily: 'monospace', color: '#4a8c42', marginBottom: '20px', wordBreak: 'break-all' },
    rulesCard: { background: '#080f08', border: '1px solid #0f1e0f', borderRadius: '16px', padding: '20px', fontSize: '12px', color: '#4a7842', lineHeight: '1.9' },
  };

  // ── SUCCESS SCREEN ────────────────────────────────────────────────────
  if (submitted) {
    return (
      <div style={S.page}>
        <style>{`@keyframes pulse{0%,100%{opacity:1}50%{opacity:0.3}}`}</style>
        <div style={S.header}>
          <h1 style={S.title}>Deposit</h1>
        </div>
        <div style={S.container}>
          <div style={S.successCard}>
            <div style={{ fontSize: '52px', marginBottom: '14px' }}>✅</div>
            <div style={{ fontSize: '18px', fontWeight: 700, color: '#4ade80', marginBottom: '8px' }}>Order submitted!</div>
            <div style={{ fontSize: '13px', color: '#7aaa72', lineHeight: '1.7', marginBottom: '20px' }}>
              Your transaction is being verified on the Polygon network.<br />
              Diamonds will be credited automatically once confirmed.<br />
              This usually takes 1–3 minutes.
            </div>
            <div style={S.orderId}>Order ID: {orderId}</div>
            {polygonscanLink && (
              <a href={polygonscanLink} target="_blank" rel="noopener noreferrer"
                style={{ ...S.txLink, justifyContent: 'center', display: 'flex', marginBottom: '20px' }}>
                🔍 View on Polygonscan ↗
              </a>
            )}
            <div>
              <button style={{ background: '#1a3c18', border: '1px solid #2a5028', color: '#7ecb6e', borderRadius: '10px', padding: '12px 20px', cursor: 'pointer', fontSize: '14px', fontWeight: 600, marginRight: '10px' }}
                onClick={() => navigate('/account', { state: { tab: 'history' } })}>
                📋 History
              </button>
              <button style={{ background: '#22c55e', border: 'none', color: '#071309', borderRadius: '10px', padding: '12px 20px', cursor: 'pointer', fontSize: '14px', fontWeight: 700 }}
                onClick={() => navigate('/')}>
                Go Home
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // ── MAIN RENDER ───────────────────────────────────────────────────────
  return (
    <div style={S.page}>
      <style>{`
        @keyframes pulse{0%,100%{opacity:1}50%{opacity:0.3}}
        @keyframes urgentPulse{0%,100%{opacity:1}50%{opacity:0.6}}
        input::placeholder{color:#3a6038}
        input:focus{border-color:#4ade80!important;box-shadow:0 0 0 2px rgba(74,222,128,0.15)}
      `}</style>

      <div style={S.header}>
        <button style={S.backBtn} onClick={() => navigate(-1)}>← Back</button>
        <h1 style={S.title}>Deposit — Polygon POL</h1>
      </div>

      <div style={S.container}>

        {/* Order Summary */}
        <div style={S.card}>
          <div style={S.label}>Order summary</div>
          <div style={S.row}>
            <span style={S.rowLabel}>You receive</span>
            <span style={S.rowValue}><span style={{ color: '#4ade80' }}>{pkg?.diamonds?.toLocaleString() ?? "..."}</span> 💎 diamonds</span>
          </div>
          <div style={S.row}>
            <span style={S.rowLabel}>Network</span>
            <span style={S.rowValue}>Polygon (MATIC)</span>
          </div>
          <div style={S.row}>
            <span style={S.rowLabel}>Token</span>
            <span style={S.rowValue}>POL</span>
          </div>
          <div style={{ ...S.row, borderBottom: 'none' }}>
            <span style={S.rowLabel}>USD value</span>
            <span style={S.rowValue}>${pkg?.usdPrice?.toFixed(2) ?? "..."}</span>
          </div>

          {/* BNB Amount box */}
          <div style={S.amountBox}>
            <div>
              <div style={S.amountValue}>
                {exactAmount ? `${exactAmount} POL` : rateLoading ? 'Fetching rate...' : 'Awaiting rate...'}
              </div>
              <div style={S.amountSub}>
                {bnbPrice ? `1 POL ≈ $${bnbPrice.toLocaleString()}` : 'Fetching live rate...'}
              </div>
            </div>

          </div>

          {/* Urgent timer */}
          <div style={S.timerBox}>
            <span style={S.timerDot} />
            <span style={S.timerText}>
              {isUrgent ? '⚠️ Rate expiring soon — pay now!' : 'Rate refreshes in'}
            </span>
            <span style={S.timerCount}>{formatTimer(rateTimer)}</span>
            <button style={S.refreshBtn} onClick={fetchRate}>↺</button>
          </div>
        </div>

        {/* Warning + Rules ABOVE QR */}
        <div style={S.warning}>
          ⚠️ <strong>Important:</strong> Send exactly <strong>{exactAmount ? `${exactAmount} POL` : '...'}</strong> on the <strong>Polygon network only.</strong><br />
          Sending on the wrong network will result in permanent loss of funds.<br />
          Underpayments stay pending — top up to complete the order.
        </div>

        <div style={{ ...S.rulesCard, marginBottom: '16px' }}>
          <div style={S.label}>Payment rules</div>
          ✅ <strong style={{ color: '#e8f5e3' }}>Exact amount</strong> → diamonds credited instantly<br />
          ⚠️ <strong style={{ color: '#e8f5e3' }}>Underpayment</strong> → stays pending, send remaining to complete<br />
          💸 <strong style={{ color: '#e8f5e3' }}>Overpayment</strong> → ordered 💎 credited + excess as platform balance<br />
          ❌ <strong style={{ color: '#e8f5e3' }}>Wrong token</strong> → flagged for manual review<br />
          ⏱ <strong style={{ color: '#e8f5e3' }}>No payment in 24h</strong> → order expires automatically
        </div>

        {/* QR + Address */}
        <div style={S.card}>
          <div style={S.label}>Send to this address</div>
          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '14px' }}>
            <div style={S.qrWrapper}>
              <canvas ref={qrCanvasRef} />
            </div>
            <div style={S.walletRow}>
              <span style={S.walletAddr}>{PLATFORM_WALLET}</span>
              <button style={S.copyBtn} onClick={copyAddress}>
                {copied ? '✓ Copied' : 'Copy'}
              </button>
            </div>
          </div>
        </div>



        {/* TX Hash */}
        <div style={{ ...S.card, marginTop: '16px' }}>
          <div style={S.label}>Confirm your payment</div>
          <p style={{ fontSize: '13px', color: '#7aaa72', marginBottom: '12px', lineHeight: '1.6' }}>
            After sending, paste your transaction hash below. Find it in your wallet's transaction history.
          </p>
          <input
            style={S.txInput}
            placeholder="0x... paste transaction hash here"
            value={txHash}
            onChange={e => { setTxHash(e.target.value); setTxError(''); }}
          />
          {polygonscanLink && (
            <a href={polygonscanLink} target="_blank" rel="noopener noreferrer" style={S.txLink}>
              🔍 Verify on Polygonscan ↗
            </a>
          )}
          {txError && <div style={S.errorText}>⚠ {txError}</div>}
          <button style={S.submitBtn} onClick={handleSubmit} disabled={submitting}>
            {submitting ? 'Submitting...' : "I've sent the payment →"}
          </button>
        </div>



      </div>
    </div>
  );
}
